import styled from "@emotion/styled";
import { Segmented } from "antd";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { SellerReportPeriod } from "services/interface/sellerReport.interface";
import { numberFormatInt } from "utils/nuberFormat";
import { formatTrendAxisLabel, formatTrendTooltipLabel } from "utils/thaiDate";
import { useSellerSalesTrend } from "hooks/useSellerReport";
import { PERIOD_OPTIONS } from "../header/PeriodDropdown";
import SectionCard from "../shared/SectionCard";
import { sd } from "../tokens";

interface SalesTrendCardProps {
  period: SellerReportPeriod;
  onPeriodChange: (period: SellerReportPeriod) => void;
}

/** ย่อหน่วยบนแกน Y ให้ป้ายไม่ล้น: 48650 → 48.7K */
const compactBaht = (value: number): string => {
  if (!Number.isFinite(value)) return "0";
  if (Math.abs(value) >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (Math.abs(value) >= 1_000) return `${(value / 1_000).toFixed(1)}K`;
  return `${value}`;
};

const SalesTrendCard = ({ period, onPeriodChange }: SalesTrendCardProps) => {
  const { data, isPending, isFetching, isError, refetch } = useSellerSalesTrend(period);
  const points = data?.points ?? [];
  const growth = data?.growth_pct ?? 0;

  return (
    <SectionCard
      eyebrow="ผลการขาย"
      title="แนวโน้มยอดขาย"
      minHeight={360}
      skeletonRows={6}
      loading={isPending}
      refreshing={isFetching && !isPending}
      error={isError ? true : undefined}
      onRetry={() => refetch()}
      extra={
        <Segmented
          size="small"
          value={period}
          onChange={(value) => onPeriodChange(value as SellerReportPeriod)}
          options={PERIOD_OPTIONS.map((option) => ({ label: option.label, value: option.key }))}
        />
      }
    >
      <Summary>
        <Total>฿{numberFormatInt(data?.total ?? 0)}</Total>
        <Growth positive={growth >= 0}>
          {growth >= 0 ? "↗" : "↘"} {growth >= 0 ? "เติบโต" : "ลดลง"} {Math.abs(growth).toFixed(1)}%
        </Growth>
      </Summary>

      <ChartBox>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={points} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
            <defs>
              <linearGradient id="sdTrendFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={sd.orange} stopOpacity={0.22} />
                <stop offset="100%" stopColor={sd.orange} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={sd.border} />
            <XAxis
              dataKey="label"
              tickLine={false}
              axisLine={false}
              interval="preserveStartEnd"
              minTickGap={24}
              tick={{ fontSize: 12, fill: sd.subtle }}
              tickFormatter={(label: string) => formatTrendAxisLabel(label, period)}
            />
            <YAxis
              width={52}
              tickLine={false}
              axisLine={false}
              tick={{ fontSize: 12, fill: sd.subtle }}
              tickFormatter={compactBaht}
            />
            <Tooltip
              cursor={{ stroke: sd.border }}
              formatter={(value: number) => [`฿${numberFormatInt(value)}`, "ยอดขาย"]}
              labelFormatter={(label: string) => formatTrendTooltipLabel(label, period)}
              contentStyle={{ borderRadius: 8, borderColor: sd.border, fontSize: 13 }}
            />
            <Area
              type="monotone"
              dataKey="amount"
              stroke={sd.orange}
              strokeWidth={2}
              fill="url(#sdTrendFill)"
              dot={false}
              activeDot={{ r: 4 }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </ChartBox>
    </SectionCard>
  );
};

export default SalesTrendCard;

const Summary = styled.div`
  display: flex;
  align-items: baseline;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 8px;
`;

const Total = styled.div`
  font-size: 28px;
  font-weight: 700;
  color: ${sd.navy};
`;

const Growth = styled.span<{ positive: boolean }>`
  font-size: 13px;
  font-weight: 600;
  color: ${(p) => (p.positive ? sd.green : sd.red)};
`;

const ChartBox = styled.div`
  width: 100%;
  height: 260px;

  @media (max-width: 575px) {
    height: 200px;
  }
`;
